// btw-front/src/pages/PartnerPerformance.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import { StatCard } from "../components/ui/StatCard";
import { EmptyState } from "../components/ui/EmptyState";
import { Button } from "../components/ui/Button";
import { MiniBar } from "../components/charts/MiniBar";
import { MiniDonut } from "../components/charts/MiniDonut";
import { usePartnerSession } from "../context/PartnerSessionContext";
import { get as getPerformance } from "../api/partnerPerformance";
import { get as getAdminPerformance } from "../api/adminPerformance";
import { SALES_STAGES } from "../constants/dealStatus";
import { formatQuarter } from "../lib/format";
import { useDealRegistrationGate } from "../lib/dealRegistrationGate";

function formatKRW(n) {
  return new Intl.NumberFormat("ko-KR").format(n || 0);
}

// 영업단계 7색 — Badge.jsx 톤과 톤은 다르지만 같은 의미 묶음
const STATUS_COLOR = {
  brochure_sent: "#94a3b8",
  intro: "#0ea5e9",
  prelim_quote: "#06b6d4",
  poc: "#8b5cf6",
  proposal: "#6366f1",
  adoption: "#10b981",
  lost: "#f43f5e",
};

const EMPTY_KPI = { total: 0, inProgress: 0, adoption: 0, lost: 0, poAmount: 0 };

export default function PartnerPerformance() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { goToNewDeal } = useDealRegistrationGate();
  const { role, profile } = usePartnerSession();
  const [searchParams] = useSearchParams();

  // 관리자 모드 — ?partnerId= 로 특정 파트너 조회
  const queriedPartnerId = role === "admin" ? searchParams.get("partnerId") : null;
  const isAdminMode = !!queriedPartnerId;

  const [data, setData] = useState({
    kpi: EMPTY_KPI,
    byQuarter: [],
    topCustomers: [],
    recentActivity: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    // admin 모드(?partnerId) 면 admin endpoint, 그 외 본인 partner endpoint
    const promise = queriedPartnerId ? getAdminPerformance(queriedPartnerId) : getPerformance();
    promise
      .then((res) => {
        if (!cancelled)
          setData({
            ...{ kpi: EMPTY_KPI, byQuarter: [], topCustomers: [], recentActivity: [] },
            ...res,
          });
      })
      .catch(() => {
        if (!cancelled)
          setData({ kpi: EMPTY_KPI, byQuarter: [], topCustomers: [], recentActivity: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [queriedPartnerId]);

  const kpi = data.kpi || EMPTY_KPI;

  // 서버 응답의 byStatus(영업단계 7개 분포) 사용. 응답 없으면 7개 0 으로 폴백.
  const byStatus = useMemo(() => {
    const counts = Object.fromEntries((data.byStatus || []).map((b) => [b.status, b.value]));
    return SALES_STAGES.map((s) => ({
      label: t(`partners.deals.status.${s}`),
      value: counts[s] || 0,
      color: STATUS_COLOR[s],
    }));
  }, [data.byStatus, t]);

  // 관리자 모드면 응답의 파트너 회사명을, 그 외(본인 파트너)는 세션 profile.company.
  // profile.company 는 관리자 본인 회사라 admin 모드에서 그대로 쓰면 잘못 표시됨.
  const companyName = isAdminMode ? data.partner?.companyName || "" : profile.company || "";

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          {isAdminMode && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <span>{t("partners.performance.adminBanner", { company: companyName })}</span>
              <button
                type="button"
                onClick={() => navigate("/admin")}
                className="text-xs font-medium text-amber-900 underline-offset-2 hover:underline"
              >
                {t("partners.performance.adminBannerBack")} →
              </button>
            </div>
          )}

          <header className="flex flex-wrap items-end justify-between gap-3">
            <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
              {t("partners.performance.title", { company: companyName })}
            </h1>
            <div className="flex flex-wrap gap-2">
              <Link to="/partners/dashboard" className="btw-btn-ghost">
                ← {t("partners.deals.detail.backToList")}
              </Link>
              <button
                type="button"
                onClick={() => message.info(t("partners.performance.exportSoon"))}
                className="btw-btn-ghost"
              >
                {t("partners.performance.exportCsv")}
              </button>
            </div>
          </header>

          {loading ? (
            <p className="mt-10 text-center text-sm text-slate-400">
              {t("partners.common.loading")}
            </p>
          ) : kpi.total === 0 ? (
            <div className="mt-8">
              <EmptyState
                title={t("partners.performance.emptyTitle")}
                desc={t("partners.performance.emptyDesc")}
                cta={
                  <Button variant="accent" onClick={goToNewDeal}>
                    {t("partners.performance.emptyCta")}
                  </Button>
                }
              />
            </div>
          ) : (
            <>
              <section className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
                <StatCard label={t("partners.performance.kpi.total")} value={kpi.total} />
                <StatCard label={t("partners.performance.kpi.inProgress")} value={kpi.inProgress} />
                <StatCard label={t("partners.performance.kpi.adoption")} value={kpi.adoption} />
                <StatCard label={t("partners.performance.kpi.lost")} value={kpi.lost} />
                <StatCard
                  accent
                  label={t("partners.performance.kpi.poAmount")}
                  value={`${formatKRW(kpi.poAmount)} ${t("common.unitKrw")}`}
                />
              </section>

              <section className="mt-6 grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <h2 className="text-sm font-semibold text-slate-800">
                    {t("partners.performance.charts.byQuarter")}
                  </h2>
                  <div className="mt-4">
                    {data.byQuarter.length === 0 ? (
                      <p className="text-xs text-slate-400">-</p>
                    ) : (
                      <MiniBar
                        data={data.byQuarter.map((b) => ({
                          ...b,
                          label: formatQuarter(b.label) || b.label,
                        }))}
                      />
                    )}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <h2 className="text-sm font-semibold text-slate-800">
                    {t("partners.performance.charts.byStatus")}
                  </h2>
                  <div className="mt-4 flex items-center justify-center">
                    <MiniDonut data={byStatus} centerLabel={t("partners.performance.kpi.total")} />
                  </div>
                </div>
              </section>

              <section className="mt-6 grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <h2 className="text-sm font-semibold text-slate-800">
                    {t("partners.performance.charts.topCustomers")}
                  </h2>
                  <ul className="mt-3 divide-y divide-slate-100">
                    {data.topCustomers.map((c, i) => (
                      <li key={c.name}>
                        {/* 고객사별 최신 딜 상세로 이동 — dealId 없으면(구 응답 캐시) 클릭 비활성 */}
                        <button
                          type="button"
                          disabled={!c.dealId}
                          onClick={() => c.dealId && navigate(`/partners/deals/${c.dealId}`)}
                          className="flex w-full items-center justify-between gap-3 rounded-lg px-1 py-2 text-left transition enabled:cursor-pointer enabled:hover:bg-slate-50"
                        >
                          <div className="flex items-center gap-2">
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-600">
                              {i + 1}
                            </span>
                            <span className="text-sm text-slate-800">{c.name}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-slate-500">
                              {c.count} {t("common.unitCount")}
                            </div>
                            <div className="text-xs font-semibold text-slate-700 tabular-nums">
                              {formatKRW(c.sum)} {t("common.unitKrw")}
                            </div>
                          </div>
                        </button>
                      </li>
                    ))}
                    {data.topCustomers.length === 0 && <p className="text-xs text-slate-400">-</p>}
                  </ul>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <h2 className="text-sm font-semibold text-slate-800">
                    {t("partners.performance.charts.recentActivity")}
                  </h2>
                  <ul className="mt-3 grid gap-2">
                    {data.recentActivity.map((a) => (
                      <li key={a.dealId}>
                        <button
                          type="button"
                          onClick={() => navigate(`/partners/deals/${a.dealId}`)}
                          className="flex w-full items-start gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 text-left text-xs transition hover:border-accent/40 hover:bg-white"
                        >
                          <span className="mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                          <div className="flex-1">
                            <div className="font-medium text-slate-800">{a.projectName}</div>
                            <div className="text-slate-500">
                              {t("partners.performance.charts.statusLabel")}:{" "}
                              {t(`partners.deals.status.${a.status}`)}
                            </div>
                          </div>
                          <span className="text-[10px] tabular-nums text-slate-400">
                            {a.createdAt && a.createdAt.slice(0, 10)}
                          </span>
                        </button>
                      </li>
                    ))}
                    {data.recentActivity.length === 0 && (
                      <p className="text-xs text-slate-400">-</p>
                    )}
                  </ul>
                </div>
              </section>
            </>
          )}
        </Container>
      </main>
      <Footer />
    </div>
  );
}
